/* =============================================================================
   MaterialCollection — one list of materials, drawn as rows or as tiles.

   There is deliberately ONE item component. Grid and list differ only by the
   modifier class on the wrapper (`.mat-collection--grid` / `--list`); the CSS
   re-lays the same elements. A change to an item lands in both views at once,
   and neither view can grow a field the other lacks.

   ONE DOCUMENT ASKS FOR ONE KIND PER LINE, and it is passed in rather than
   assumed. `oneKindPerLine` marks the first item after a KIND CHANGE so the
   grid opens a new line for it; the caller decides who gets that, and today
   only Śrī Rudram does (see ONE_KIND_PER_LINE_SLUG in DocumentPage). With the
   flag off — every other document — nothing is marked, no class is emitted,
   and the layout is byte-for-byte what it was before this existed.

   The reason it is wanted at all: a group the editor named holds whatever was
   put in it, and the Rudram's "Namakam" is ten classes, eighteen recordings and
   fourteen PDFs. A video plate is 16:9, a recording is square, a text is a
   portrait page; flowed into one track those shapes interleave and the eye
   loses the run. The editor's order is never touched — this only says where a
   run ends, never what comes next, so a kind that appears twice gets two runs.

   FOLDERS ARE ITEMS TOO. A group's folders (see `groupMaterials`) are drawn
   with the same `.mat-item` parts — thumb, title, sub-line, trailing action —
   so a folder tile and a file tile share every rule of both layouts, and the
   grid/list switch re-lays them together. Its primary control is a real link
   (`?folder=…`), so a folder opens in a new tab, survives a reload and can be
   copied out of the address bar like any other page.
   ============================================================================= */
import { Link } from "react-router-dom";
import type { To } from "react-router-dom";
import { describeContents } from "./materials";
import type { Material, MaterialFolder, MaterialGroup } from "./materials";
import { Icon } from "../ui/Icon";
import type { MaterialView } from "@vedaunion/shared";

export function MaterialCollection({
  groups,
  view,
  oneKindPerLine = false,
  playingId,
  onPlay,
  onWatch,
  folderTo,
}: {
  groups: MaterialGroup[];
  view: MaterialView;
  /** Give each run of one kind its own line in the tile grid. Opt-in, per
   *  document; off means no item is marked at all. */
  oneKindPerLine?: boolean;
  playingId: string | null;
  onPlay: (m: Material) => void;
  onWatch: (m: Material) => void;
  /** Where a folder tile leads — the page itself, with `?folder=` set. */
  folderTo: (f: MaterialFolder) => To;
}) {
  if (groups.length === 0) return null;
  return (
    <>
      {groups.map((g) => (
        <section className="mat-group" key={g.key}>
          <header className="mat-group__head">
            <h3 className="mat-group__title">{g.title}</h3>
            <span className="mat-group__count">
              {countLabel(g)}
            </span>
            <span className="mat-group__rule" aria-hidden="true" />
          </header>
          <div className={`mat-collection mat-collection--${view}`}>
            {g.folders.map((f) => (
              <FolderItem key={`folder:${f.key}`} folder={f} to={folderTo(f)} />
            ))}
            {g.items.map((m, i) => (
              <MaterialItem
                key={m.id}
                material={m}
                // Folders form a run of their own, so the first file after
                // them opens a fresh line exactly as a kind change does.
                startsKindRun={
                  oneKindPerLine &&
                  (i > 0 ? m.kind !== g.items[i - 1]?.kind : g.folders.length > 0)
                }
                isPlaying={playingId === m.id}
                onPlay={onPlay}
                onWatch={onWatch}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/** "2 folders · 6 items" — a folder is not counted as one of the files it holds. */
function countLabel(g: MaterialGroup): string {
  const parts: string[] = [];
  if (g.folders.length > 0) parts.push(`${g.folders.length} ${g.folders.length === 1 ? "folder" : "folders"}`);
  if (g.items.length > 0 || parts.length === 0) {
    parts.push(`${g.items.length} ${g.items.length === 1 ? "item" : "items"}`);
  }
  return parts.join(" · ");
}

function FolderItem({ folder: f, to }: { folder: MaterialFolder; to: To }) {
  const n = f.items.length;
  return (
    <div className="mat-item mat-item--folder">
      <span className="mat-item__thumb is-folder">
        <span className="mat-item__glyph">
          <Icon name="folder" size="1.15em" />
          <span className="mat-item__ext">
            {n} {n === 1 ? "item" : "items"}
          </span>
        </span>
      </span>
      <span className="mat-item__body">
        <span className="mat-item__title">
          <Link className="mat-item__link" to={to}>
            {f.title}
          </Link>
        </span>
        <span className="mat-item__meta">{describeContents(f.items)}</span>
      </span>
      <span className="mat-item__aside">
        {n} {n === 1 ? "item" : "items"}
      </span>
      <span className="mat-item__action" aria-hidden="true">
        <Icon name="nav-right" size="1.05em" />
      </span>
    </div>
  );
}

function MaterialItem({
  material: m,
  startsKindRun,
  isPlaying,
  onPlay,
  onWatch,
}: {
  material: Material;
  /** First item after a kind change — the grid gives it a fresh line. False for
   *  the first item of a group, which already opens one. */
  startsKindRun: boolean;
  isPlaying: boolean;
  onPlay: (m: Material) => void;
  onWatch: (m: Material) => void;
}) {
  // An item carries TWO affordances — open/play, and download — so it cannot be
  // one big <a> with another <a> inside it: nesting interactive elements is
  // invalid HTML and breaks keyboard navigation. Instead the primary action is a
  // normal link/button whose ::after stretches over the whole card, and the
  // download control sits above it on the z-axis. Both are real, focusable
  // controls; the card is still clickable anywhere.
  const primary = m.videoSource ? (
    <button type="button" className="mat-item__link" onClick={() => onWatch(m)}>
      {m.title}
    </button>
  ) : m.audioUrl ? (
    <button type="button" className="mat-item__link" onClick={() => onPlay(m)}>
      {m.title}
    </button>
  ) : (
    <a
      className="mat-item__link"
      href={m.href ?? undefined}
      target={m.href ? "_blank" : undefined}
      rel={m.href ? "noreferrer" : undefined}
    >
      {m.title}
    </a>
  );

  return (
    <div
      className={`mat-item${startsKindRun ? " mat-item--run-start" : ""}${
        isPlaying ? " is-playing" : ""
      }`}
    >
      <MaterialThumb material={m} />
      <span className="mat-item__body">
        <span className="mat-item__title">{primary}</span>
        <span className="mat-item__meta">
          {m.meta}
          {/* A tile has no trailing column, so the size/duration joins the
              sub-line there. In rows it has its own cell, and this copy is
              hidden by CSS rather than by a second component. */}
          {m.aside && (
            <span className="mat-item__meta-aside">
              {m.meta ? " · " : ""}
              {m.aside}
            </span>
          )}
        </span>
      </span>
      <span className="mat-item__aside">{m.aside}</span>
      {m.downloadUrl ? (
        <a
          className="mat-item__action"
          href={m.downloadUrl}
          download
          aria-label={`Download ${m.title}`}
          title={`Download ${m.title}`}
        >
          <Icon name="download" size="1.05em" />
        </a>
      ) : m.href ? (
        <span className="mat-item__action" aria-hidden="true">
          <Icon name="arrow-up-right" size="1.05em" />
        </span>
      ) : (
        <span className="mat-item__action" aria-hidden="true" />
      )}
    </div>
  );
}

/**
 * The poster. A real page-one render when we have one; otherwise a typed glyph
 * and the extension — which says what the file is without pretending to show it.
 */
function MaterialThumb({ material: m }: { material: Material }) {
  return (
    <span className={`mat-item__thumb is-${m.kind}`}>
      {m.thumbnailUrl ? (
        <img src={m.thumbnailUrl} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className="mat-item__glyph">
          <Icon name={m.icon} size="1.15em" />
          <span className="mat-item__ext">{m.ext}</span>
        </span>
      )}
      {m.playable && (
        <span className="mat-item__play" aria-hidden="true">
          <span>
            <Icon name="play" size="0.8em" />
          </span>
        </span>
      )}
    </span>
  );
}
